package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Les ressources d'une compétence dans le moteur de synchronisation. L'état de leur
 * analyse (index_status, index_error, indexed_at, refresh_error) est écrit par le
 * serveur seul : il arrive par le pull, et ne repart jamais de l'appareil.
 */
internal object SkillResourceSyncOps {

    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = db.skillResourceQueries.selectDirty()

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> =
        db.skillResourceQueries.selectDirty().executeAsList().map { row(it) }

    fun selectAll(entity: EntityKind, db: TeddDatabase): List<RemoteRow> =
        db.skillResourceQueries.selectAll().executeAsList().filter { it.push_error == null }.map { row(it) }

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        db.skillResourceQueries.markSynced(syncedAt, id, updatedAt)
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? =
        db.skillResourceQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }

    fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        db.skillResourceQueries.insertFromRemote(
            row.id,
            p.str("skill_id") ?: "",
            p.str("step_id"),
            p.str("origin") ?: "text",
            p.str("format") ?: "text",
            p.str("title") ?: "",
            p.str("url"),
            p.str("body"),
            p.long("order_index") ?: 0L,
            p.str("index_status"),
            p.str("index_error"),
            p.long("indexed_at"),
            p.str("refresh_error"),
            p.long("created_at") ?: row.updatedAt,
            row.updatedAt,
        )
    }

    fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        db.skillResourceQueries.updateFromRemote(
            p.str("skill_id") ?: "",
            p.str("step_id"),
            p.str("origin") ?: "text",
            p.str("format") ?: "text",
            p.str("title") ?: "",
            p.str("url"),
            p.str("body"),
            p.long("order_index") ?: 0L,
            p.str("index_status"),
            p.str("index_error"),
            p.long("indexed_at"),
            p.str("refresh_error"),
            row.updatedAt,
            row.id,
        )
    }

    fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow, deletedAt: Long) {
        db.skillResourceQueries.softDelete(deletedAt, row.updatedAt, row.id)
    }

    fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        db.skillResourceQueries.restore(row.updatedAt, row.id)
    }

    /**
     * Le serveur a refusé la ressource : elle ne repart plus, et l'écran dit pourquoi.
     * Son fichier, que le serveur n'aurait pas accepté sans elle, reste sur l'appareil
     * comme déjà envoyé, et ses octets sont libérés.
     */
    fun markRefused(db: TeddDatabase, id: String, reason: String, now: Long) {
        db.transaction {
            db.skillResourceQueries.markRefused(reason, id)
            db.attachmentQueries.selectByOwner(OWNER, id).executeAsList().forEach { db.fileBlobQueries.remove(it.id) }
            db.attachmentQueries.dropUnsent(now, now, now, OWNER, id)
        }
    }

    const val OWNER = "skill_resource"

    // Les champs du serveur n'y sont pas : il les ignorerait, et ils alourdiraient l'envoi.
    private fun row(it: com.tedd.db.Skill_resource) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "skill_id" to it.skill_id,
        "step_id" to it.step_id,
        "origin" to it.origin,
        "format" to it.format,
        "title" to it.title,
        "url" to it.url,
        "body" to it.body,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))
}
